import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Header from '../../components/Header';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, 
  Tooltip, Legend, ResponsiveContainer, LineChart, Line
} from 'recharts';
import { 
  Users, Heart, Activity, TrendingUp, AlertTriangle, CheckCircle2,
  FileText, Shield, Smartphone, Award
} from 'lucide-react';
import THRDistrictDetailModal from '../../components/THRDistrictDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const COLORS = ['#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444', '#6366f1', '#14b8a6'];

const GrowthMonitoringDashboard = () => {
  const [activeTab, setActiveTab] = useState('beneficiaries');
  const [loading, setLoading] = useState(true);
  
  // State for all data
  const [beneficiariesOverview, setBeneficiariesOverview] = useState(null);
  const [beneficiariesBySector, setBeneficiariesBySector] = useState([]);
  const [childrenByAge, setChildrenByAge] = useState([]);
  const [coverageOverview, setCoverageOverview] = useState(null);
  const [coverageBySector, setCoverageBySector] = useState([]);
  const [nutritionOverview, setNutritionOverview] = useState(null);
  const [nutritionByAge, setNutritionByAge] = useState([]);
  const [nutritionBySector, setNutritionBySector] = useState([]);
  const [sectorHotspots, setSectorHotspots] = useState([]);
  const [genderComparison, setGenderComparison] = useState([]);
  const [sample0_6m, setSample0_6m] = useState(null);
  const [priorityChildren, setPriorityChildren] = useState([]);
  const [selectedDistrict, setSelectedDistrict] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchAllData();
  }, []);

  // Helper function to get district/project identifier from sector name
  // Since data is organized by sectors, we'll use the full sector name
  // Backend will search across project, district, and sector fields
  const getDistrictIdentifier = (sectorName) => {
    if (!sectorName) return 'Unknown';
    // Return the full sector name - backend will match it against sector, project, district fields
    return sectorName;
  };

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [
        benOverview, benBySector, childrenAge,
        covOverview, covBySector,
        nutOverview, nutByAge, nutBySector,
        hotspots, gender, sample, priority
      ] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/beneficiaries/overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/beneficiaries/by-sector`),
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/beneficiaries/children-by-age`),
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/coverage/overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/coverage/by-sector`),
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/nutrition/overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/nutrition/by-age-group`),
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/nutrition/by-sector`),
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/hotspots/sectors`),
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/gender/comparison`),
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/sample-0-6m/overview`),
        axios.get(`${BACKEND_URL}/api/dashboards/growth-monitoring/priority-children?limit=50`)
      ]);

      setBeneficiariesOverview(benOverview.data);
      setBeneficiariesBySector(benBySector.data);
      setChildrenByAge(childrenAge.data);
      setCoverageOverview(covOverview.data);
      setCoverageBySector(covBySector.data);
      setNutritionOverview(nutOverview.data);
      setNutritionByAge(nutByAge.data);
      setNutritionBySector(nutBySector.data);
      setSectorHotspots(hotspots.data);
      setGenderComparison(gender.data);
      setSample0_6m(sample.data);
      setPriorityChildren(priority.data);
    } catch (error) {
      console.error('Error fetching Growth Monitoring data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-center">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading Growth Monitoring Dashboard...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Header */}
          <div className="mb-8 pt-12">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              Growth Monitoring Quality Improvement Dashboard
            </h1>
            <p className="text-gray-600">
              Comprehensive monitoring system for child nutrition, growth tracking, and early intervention - November 2025
            </p>
          </div>

          {/* Tabs */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
            <TabsList className="grid w-full grid-cols-2 md:grid-cols-4">
              <TabsTrigger value="beneficiaries">Beneficiary Universe</TabsTrigger>
              <TabsTrigger value="coverage">GM Coverage</TabsTrigger>
              <TabsTrigger value="nutrition">Nutrition Status</TabsTrigger>
              <TabsTrigger value="hotspots">Hotspots & Gender</TabsTrigger>
            </TabsList>

            {/* PAGE 1: BENEFICIARY UNIVERSE & DIGITAL READINESS */}
            <TabsContent value="beneficiaries" className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Total Active Beneficiaries</p>
                        <p className="text-2xl font-bold text-gray-900">
                          {beneficiariesOverview?.total_active_beneficiaries?.toLocaleString()}
                        </p>
                      </div>
                      <Users className="h-8 w-8 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Active Children 0-5y</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {beneficiariesOverview?.children_0_5y?.toLocaleString()}
                        </p>
                      </div>
                      <Heart className="h-8 w-8 text-purple-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Pregnant Women</p>
                        <p className="text-2xl font-bold text-pink-600">
                          {beneficiariesOverview?.by_type?.find(t => t._id === 'pregnant_woman')?.count?.toLocaleString() || 0}
                        </p>
                      </div>
                      <Activity className="h-8 w-8 text-pink-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Adolescent Girls</p>
                        <p className="text-2xl font-bold text-orange-600">
                          {beneficiariesOverview?.by_type?.find(t => t._id === 'adolescent_girl')?.count?.toLocaleString() || 0}
                        </p>
                      </div>
                      <Award className="h-8 w-8 text-orange-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Mobile Verified</p>
                        <p className="text-2xl font-bold text-green-600">
                          {beneficiariesOverview?.mobile_verified_pct}%
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {beneficiariesOverview?.mobile_verified_count?.toLocaleString()} beneficiaries
                        </p>
                      </div>
                      <Smartphone className="h-8 w-8 text-green-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">Aadhaar Verified</p>
                        <p className="text-2xl font-bold text-blue-600">
                          {beneficiariesOverview?.aadhaar_verified_pct}%
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {beneficiariesOverview?.aadhaar_verified_count?.toLocaleString()} beneficiaries
                        </p>
                      </div>
                      <Shield className="h-8 w-8 text-blue-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-600">ABHA Linked</p>
                        <p className="text-2xl font-bold text-purple-600">
                          {beneficiariesOverview?.abha_verified_pct}%
                        </p>
                        <p className="text-xs text-gray-500 mt-1">
                          {beneficiariesOverview?.abha_verified_count?.toLocaleString()} beneficiaries
                        </p>
                      </div>
                      <FileText className="h-8 w-8 text-purple-600" />
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Lactating Mothers</p>
                      <p className="text-2xl font-bold text-indigo-600">
                        {beneficiariesOverview?.by_type?.find(t => t._id === 'lactating_mother')?.count?.toLocaleString() || 0}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Charts Row 1 */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Children 0-5y by Age Group</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart 
                        data={childrenByAge.map(item => ({
                          name: item._id.replace('children_', '').replace('0m_6m', '0-6m').replace('6m_3y', '6m-3y').replace('3y_6y', '3-6y'),
                          count: item.count
                        }))}
                        margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis 
                          dataKey="name" 
                          tick={{ fontSize: 12 }}
                        />
                        <YAxis 
                          tick={{ fontSize: 12 }}
                          width={60}
                        />
                        <Tooltip />
                        <Bar dataKey="count" fill="#8b5cf6" name="Children" />
                      </BarChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Beneficiaries by Type</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
                        <Pie
                          data={beneficiariesOverview?.by_type || []}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => {
                            const percent = (entry.count / ((beneficiariesOverview?.by_type || []).reduce((sum, e) => sum + e.count, 0) || 1)) * 100;
                            // Only show label if percentage is >= 5% to avoid overlapping
                            if (percent >= 5) {
                              return `${entry._id.split('_').pop()}: ${entry.count}`;
                            }
                            return '';
                          }}
                          outerRadius={90}
                          innerRadius={0}
                          fill="#8884d8"
                          dataKey="count"
                        >
                          {(beneficiariesOverview?.by_type || []).map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip 
                          formatter={(value, name, props) => {
                            const total = (beneficiariesOverview?.by_type || []).reduce((sum, e) => sum + e.count, 0);
                            const percent = total > 0 ? ((value / total) * 100).toFixed(1) : 0;
                            return [`${value} (${percent}%)`, props.payload._id.split('_').join(' ')];
                          }}
                        />
                        <Legend 
                          wrapperStyle={{ paddingTop: '20px' }}
                          iconSize={12}
                          style={{ fontSize: '12px' }}
                        />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Beneficiaries by Sector */}
              <Card>
                <CardHeader>
                  <CardTitle>Beneficiary Distribution by Sector</CardTitle>
                  <CardDescription>Click on a bar to view district details</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={400}>
                    <BarChart 
                      data={beneficiariesBySector}
                      margin={{ top: 20, right: 30, left: 100, bottom: 80 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis 
                        dataKey="sector" 
                        angle={-45}
                        textAnchor="end"
                        height={80}
                        tick={{ fontSize: 10 }}
                        interval={0}
                      />
                      <YAxis 
                        tick={{ fontSize: 12 }}
                        width={100}
                      />
                      <Tooltip 
                        contentStyle={{ fontSize: '12px' }}
                      />
                      <Legend 
                        wrapperStyle={{ paddingTop: '10px' }}
                        iconSize={12}
                        style={{ fontSize: '12px' }}
                      />
                      <Bar 
                        dataKey="total" 
                        fill="#3b82f6" 
                        name="Total Beneficiaries"
                        shape={(props) => {
                          const { payload, x, y, width, height } = props;
                          const [isHovered, setIsHovered] = React.useState(false);
                          
                          const handleClick = (e) => {
                            e.stopPropagation();
                            // Extract district from sector name (sectors typically have district prefix)
                            // If sector name contains a district, extract it; otherwise use sector name
                            const districtId = getDistrictIdentifier(payload?.sector);
                            setSelectedDistrict({ district: districtId, district_id: districtId });
                            setIsModalOpen(true);
                          };

                          return (
                            <rect
                              x={x}
                              y={y}
                              width={width}
                              height={height}
                              fill={isHovered ? '#2563eb' : '#3b82f6'}
                              style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                              onClick={handleClick}
                              onMouseEnter={() => setIsHovered(true)}
                              onMouseLeave={() => setIsHovered(false)}
                            />
                          );
                        }}
                        cursor="pointer"
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Digital Identity Status */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Mobile Verification</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-green-600 mb-2">
                        {beneficiariesOverview?.mobile_verified_pct}%
                      </div>
                      <p className="text-sm text-gray-600">
                        {beneficiariesOverview?.mobile_verified_count?.toLocaleString()} / {beneficiariesOverview?.total_active_beneficiaries?.toLocaleString()}
                      </p>
                      <div className="mt-4 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-green-600 h-2 rounded-full"
                          style={{ width: `${beneficiariesOverview?.mobile_verified_pct}%` }}
                        ></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Aadhaar Verification</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-blue-600 mb-2">
                        {beneficiariesOverview?.aadhaar_verified_pct}%
                      </div>
                      <p className="text-sm text-gray-600">
                        {beneficiariesOverview?.aadhaar_verified_count?.toLocaleString()} / {beneficiariesOverview?.total_active_beneficiaries?.toLocaleString()}
                      </p>
                      <div className="mt-4 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-blue-600 h-2 rounded-full"
                          style={{ width: `${beneficiariesOverview?.aadhaar_verified_pct}%` }}
                        ></div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>ABHA Linkage</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center">
                      <div className="text-4xl font-bold text-purple-600 mb-2">
                        {beneficiariesOverview?.abha_verified_pct}%
                      </div>
                      <p className="text-sm text-gray-600">
                        {beneficiariesOverview?.abha_verified_count?.toLocaleString()} / {beneficiariesOverview?.total_active_beneficiaries?.toLocaleString()}
                      </p>
                      <div className="mt-4 bg-gray-200 rounded-full h-2">
                        <div 
                          className="bg-purple-600 h-2 rounded-full"
                          style={{ width: `${beneficiariesOverview?.abha_verified_pct}%` }}
                        ></div>
                      </div>
                      <p className="text-xs text-orange-600 mt-2">🎯 Opportunity for scale-up</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </TabsContent>

            {/* PAGE 2: GROWTH MONITORING COVERAGE */}
            <TabsContent value="coverage" className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Active Children 0-5y</p>
                      <p className="text-3xl font-bold text-gray-900">
                        {coverageOverview?.active_children?.toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Growth Monitored</p>
                      <p className="text-3xl font-bold text-green-600">
                        {coverageOverview?.monitored_children?.toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Overall Coverage</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {coverageOverview?.overall_coverage_pct}%
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Not Monitored</p>
                      <p className="text-3xl font-bold text-red-600">
                        {(coverageOverview?.active_children - coverageOverview?.monitored_children)?.toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Coverage by Age Group */}
              <Card>
                <CardHeader>
                  <CardTitle>Growth Monitoring Coverage by Age Group</CardTitle>
                  <CardDescription>Active children vs monitored children comparison</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={coverageOverview?.coverage_by_age || []}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="age_group" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="active" fill="#cbd5e1" name="Active Children" />
                      <Bar dataKey="monitored" fill="#10b981" name="Monitored Children" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Coverage Percentage by Age */}
              <Card>
                <CardHeader>
                  <CardTitle>Coverage % by Age Group</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {(coverageOverview?.coverage_by_age || []).map((item, index) => (
                      <div key={index}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-sm font-medium text-gray-700">{item.age_group}</span>
                          <span className="text-sm font-bold text-gray-900">{item.coverage_pct}%</span>
                        </div>
                        <div className="bg-gray-200 rounded-full h-3">
                          <div 
                            className={`h-3 rounded-full ${item.coverage_pct >= 85 ? 'bg-green-600' : item.coverage_pct >= 75 ? 'bg-yellow-500' : 'bg-red-600'}`}
                            style={{ width: `${item.coverage_pct}%` }}
                          ></div>
                        </div>
                        <div className="mt-1 text-xs text-gray-600">
                          {item.monitored?.toLocaleString()} / {item.active?.toLocaleString()} children
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Coverage by Sector */}
              <Card>
                <CardHeader>
                  <CardTitle>Growth Monitoring Coverage by Sector</CardTitle>
                  <CardDescription>Sectors sorted by coverage percentage (lowest first) - Click on a bar to view district details</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart 
                      data={coverageBySector} 
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 120, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <YAxis 
                        dataKey="sector" 
                        type="category" 
                        width={120}
                        tick={{ fontSize: 10 }}
                      />
                      <Tooltip 
                        contentStyle={{ fontSize: '12px' }}
                      />
                      <Bar 
                        dataKey="coverage_pct" 
                        fill="#3b82f6" 
                        name="Coverage %"
                        shape={(props) => {
                          const { payload, x, y, width, height } = props;
                          const [isHovered, setIsHovered] = React.useState(false);
                          
                          const handleClick = (e) => {
                            e.stopPropagation();
                            const districtId = getDistrictIdentifier(payload?.sector);
                            setSelectedDistrict({ district: districtId, district_id: districtId });
                            setIsModalOpen(true);
                          };

                          const fillColor = payload?.coverage_pct < 85 ? '#ef4444' : '#10b981';
                          const hoverColor = payload?.coverage_pct < 85 ? '#dc2626' : '#059669';

                          return (
                            <rect
                              x={x}
                              y={y}
                              width={width}
                              height={height}
                              fill={isHovered ? hoverColor : fillColor}
                              style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                              onClick={handleClick}
                              onMouseEnter={() => setIsHovered(true)}
                              onMouseLeave={() => setIsHovered(false)}
                            />
                          );
                        }}
                        cursor="pointer"
                      >
                        {coverageBySector.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.coverage_pct < 85 ? '#ef4444' : '#10b981'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Low Coverage Alert */}
              <Card className="border-red-200 bg-red-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-red-800">
                    <AlertTriangle className="h-5 w-5" />
                    Sectors Needing Attention (Coverage &lt; 85%)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {coverageBySector.filter(s => s.coverage_pct < 85).map((sector, index) => (
                      <div 
                        key={index} 
                        className="bg-white p-4 rounded-lg border border-red-200 cursor-pointer hover:border-red-400 hover:shadow-md transition-all"
                        onClick={() => {
                          const districtId = getDistrictIdentifier(sector.sector);
                          setSelectedDistrict({ district: districtId, district_id: districtId });
                          setIsModalOpen(true);
                        }}
                      >
                        <h4 className="font-semibold text-gray-900 mb-2">{sector.sector}</h4>
                        <div className="space-y-1 text-sm">
                          <p className="text-gray-600">Coverage: <span className="font-bold text-red-600">{sector.coverage_pct}%</span></p>
                          <p className="text-gray-600">Monitored: {sector.monitored?.toLocaleString()} / {sector.active?.toLocaleString()}</p>
                          <p className="text-gray-600">Gap: <span className="font-bold">{(sector.active - sector.monitored)?.toLocaleString()} children</span></p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* PAGE 3: NUTRITION STATUS */}
            <TabsContent value="nutrition" className="space-y-6">
              {/* KPI Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Total Stunted</p>
                      <p className="text-3xl font-bold text-orange-600">
                        {nutritionOverview?.stunting?.stunted_pct}%
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {nutritionOverview?.stunting?.total_stunted?.toLocaleString()} children
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Severe Stunting</p>
                      <p className="text-3xl font-bold text-red-600">
                        {nutritionOverview?.stunting?.severe_pct}%
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {nutritionOverview?.stunting?.severe?.toLocaleString()} children
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Total Underweight</p>
                      <p className="text-3xl font-bold text-yellow-600">
                        {nutritionOverview?.underweight?.underweight_pct}%
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {nutritionOverview?.underweight?.total_underweight?.toLocaleString()} children
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Wasted (MAM+SAM)</p>
                      <p className="text-3xl font-bold text-purple-600">
                        {nutritionOverview?.wasting?.wasted_pct}%
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {nutritionOverview?.wasting?.total_wasted?.toLocaleString()} children
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">SAM Cases</p>
                      <p className="text-3xl font-bold text-red-700">
                        {nutritionOverview?.wasting?.sam_pct}%
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {nutritionOverview?.wasting?.sam?.toLocaleString()} children
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Overnutrition</p>
                      <p className="text-3xl font-bold text-blue-600">
                        {nutritionOverview?.wasting?.overnutrition_pct}%
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {nutritionOverview?.wasting?.overnutrition?.toLocaleString()} children
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Any Undernutrition</p>
                      <p className="text-3xl font-bold text-red-600">
                        {nutritionOverview?.any_undernutrition_pct}%
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        {nutritionOverview?.any_undernutrition?.toLocaleString()} children
                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm font-medium text-gray-600 mb-2">Total Monitored</p>
                      <p className="text-3xl font-bold text-gray-900">
                        {nutritionOverview?.total_monitored?.toLocaleString()}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Stunting Distribution */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Stunting Status Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Normal', value: nutritionOverview?.stunting?.normal || 0, fill: '#10b981' },
                            { name: 'Moderate', value: nutritionOverview?.stunting?.moderate || 0, fill: '#f59e0b' },
                            { name: 'Severe', value: nutritionOverview?.stunting?.severe || 0, fill: '#ef4444' }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => `${entry.name}: ${entry.value}`}
                          outerRadius={80}
                          dataKey="value"
                        >
                          {[
                            { name: 'Normal', value: nutritionOverview?.stunting?.normal || 0 },
                            { name: 'Moderate', value: nutritionOverview?.stunting?.moderate || 0 },
                            { name: 'Severe', value: nutritionOverview?.stunting?.severe || 0 }
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Wasting Status Distribution</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ResponsiveContainer width="100%" height={300}>
                      <PieChart>
                        <Pie
                          data={[
                            { name: 'Normal', value: nutritionOverview?.wasting?.normal || 0, fill: '#10b981' },
                            { name: 'MAM', value: nutritionOverview?.wasting?.mam || 0, fill: '#f59e0b' },
                            { name: 'SAM', value: nutritionOverview?.wasting?.sam || 0, fill: '#ef4444' },
                            { name: 'Overweight', value: nutritionOverview?.wasting?.overweight || 0, fill: '#3b82f6' },
                            { name: 'Obese', value: nutritionOverview?.wasting?.obese || 0, fill: '#6366f1' }
                          ]}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={(entry) => entry.value > 0 ? `${entry.name}: ${entry.value}` : ''}
                          outerRadius={80}
                          dataKey="value"
                        >
                          {[
                            { fill: '#10b981' },
                            { fill: '#f59e0b' },
                            { fill: '#ef4444' },
                            { fill: '#3b82f6' },
                            { fill: '#6366f1' }
                          ].map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.fill} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </CardContent>
                </Card>
              </div>

              {/* Nutrition by Age Group */}
              <Card>
                <CardHeader>
                  <CardTitle>Stunting by Age Group</CardTitle>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={nutritionByAge}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="age_group" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="stunted_moderate_pct" fill="#f59e0b" name="Moderate %" stackId="a" />
                      <Bar dataKey="stunted_severe_pct" fill="#ef4444" name="Severe %" stackId="a" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Nutrition by Sector Heatmap */}
              <Card>
                <CardHeader>
                  <CardTitle>Nutrition Status by Sector</CardTitle>
                  <CardDescription>Sectors sorted by stunting prevalence (highest first)</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">Sector</th>
                          <th className="px-4 py-3 text-center font-semibold">Total</th>
                          <th className="px-4 py-3 text-center font-semibold">Stunted %</th>
                          <th className="px-4 py-3 text-center font-semibold">Underweight %</th>
                          <th className="px-4 py-3 text-center font-semibold">Wasted %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {nutritionBySector.map((sector, index) => (
                          <tr 
                            key={index} 
                            className="border-b hover:bg-gray-50 cursor-pointer"
                        onClick={() => {
                          const districtId = getDistrictIdentifier(sector.sector);
                          setSelectedDistrict({ district: districtId, district_id: districtId });
                          setIsModalOpen(true);
                        }}
                          >
                            <td className="px-4 py-3 font-medium">{sector.sector}</td>
                            <td className="px-4 py-3 text-center">{sector.total}</td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-1 rounded ${sector.stunted_pct > 35 ? 'bg-red-100 text-red-800' : sector.stunted_pct > 25 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                {sector.stunted_pct}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-1 rounded ${sector.underweight_pct > 20 ? 'bg-red-100 text-red-800' : sector.underweight_pct > 12 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                {sector.underweight_pct}%
                              </span>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className={`px-2 py-1 rounded ${sector.wasted_pct > 5 ? 'bg-red-100 text-red-800' : sector.wasted_pct > 3 ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                {sector.wasted_pct}%
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            {/* PAGE 4: HOTSPOTS, GENDER & PRIORITY CHILDREN */}
            <TabsContent value="hotspots" className="space-y-6">
              {/* Sector Hotspots */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    Sector Hotspots - Any Undernutrition
                  </CardTitle>
                  <CardDescription>Sectors with highest burden of undernutrition (stunting OR underweight OR wasting) - Click on a bar to view district details</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart 
                      data={sectorHotspots} 
                      layout="vertical"
                      margin={{ top: 20, right: 30, left: 120, bottom: 20 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" domain={[0, 100]} tick={{ fontSize: 12 }} />
                      <YAxis 
                        dataKey="sector" 
                        type="category" 
                        width={120}
                        tick={{ fontSize: 10 }}
                      />
                      <Tooltip 
                        contentStyle={{ fontSize: '12px' }}
                      />
                      <Bar 
                        dataKey="any_undernutrition_pct" 
                        fill="#ef4444" 
                        name="Any Undernutrition %"
                        shape={(props) => {
                          const { payload, x, y, width, height } = props;
                          const [isHovered, setIsHovered] = React.useState(false);
                          
                          const handleClick = (e) => {
                            e.stopPropagation();
                            const districtId = getDistrictIdentifier(payload?.sector);
                            setSelectedDistrict({ district: districtId, district_id: districtId });
                            setIsModalOpen(true);
                          };

                          const pct = payload?.any_undernutrition_pct || 0;
                          const fillColor = pct > 40 ? '#991b1b' : pct > 30 ? '#ef4444' : '#f59e0b';
                          const hoverColor = pct > 40 ? '#7f1d1d' : pct > 30 ? '#dc2626' : '#d97706';

                          return (
                            <rect
                              x={x}
                              y={y}
                              width={width}
                              height={height}
                              fill={isHovered ? hoverColor : fillColor}
                              style={{ cursor: 'pointer', transition: 'all 0.2s', opacity: isHovered ? 0.8 : 1 }}
                              onClick={handleClick}
                              onMouseEnter={() => setIsHovered(true)}
                              onMouseLeave={() => setIsHovered(false)}
                            />
                          );
                        }}
                        cursor="pointer"
                      >
                        {sectorHotspots.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.any_undernutrition_pct > 40 ? '#991b1b' : entry.any_undernutrition_pct > 30 ? '#ef4444' : '#f59e0b'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Red Zone Sectors */}
              <Card className="border-red-300 bg-red-50">
                <CardHeader>
                  <CardTitle className="text-red-800">🔴 Red Zone Sectors (Undernutrition &gt; 40%)</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {sectorHotspots.filter(s => s.any_undernutrition_pct > 40).map((sector, index) => (
                      <div 
                        key={index} 
                        className="bg-white p-6 rounded-lg border-2 border-red-300 cursor-pointer hover:border-red-500 hover:shadow-lg transition-all"
                        onClick={() => {
                          const districtId = getDistrictIdentifier(sector.sector);
                          setSelectedDistrict({ district: districtId, district_id: districtId });
                          setIsModalOpen(true);
                        }}
                      >
                        <h3 className="text-xl font-bold text-gray-900 mb-3">{sector.sector}</h3>
                        <div className="space-y-2">
                          <div className="flex justify-between">
                            <span className="text-gray-600">Any Undernutrition:</span>
                            <span className="font-bold text-red-700">{sector.any_undernutrition_pct}%</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-600">Affected Children:</span>
                            <span className="font-bold">{sector.any_undernutrition} / {sector.total}</span>
                          </div>
                          <div className="mt-3 p-2 bg-red-100 rounded text-xs text-red-800">
                            ⚠️ Priority intervention required
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Gender Comparison */}
              <Card>
                <CardHeader>
                  <CardTitle>Gender-wise Nutrition Comparison</CardTitle>
                  <CardDescription>Comparing undernutrition prevalence between boys and girls</CardDescription>
                </CardHeader>
                <CardContent>
                  <ResponsiveContainer width="100%" height={350}>
                    <BarChart data={genderComparison}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="gender" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="any_undernutrition_pct" fill="#8b5cf6" name="Any Undernutrition %" />
                      <Bar dataKey="severe_stunting_pct" fill="#ef4444" name="Severe Stunting %" />
                      <Bar dataKey="severe_underweight_pct" fill="#f59e0b" name="Severe Underweight %" />
                      <Bar dataKey="sam_pct" fill="#dc2626" name="SAM %" />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>

              {/* Gender Stats Table */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {genderComparison.map((item, index) => (
                  <Card key={index}>
                    <CardHeader>
                      <CardTitle>{item.gender} Children</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Total Monitored:</span>
                          <span className="font-bold text-lg">{item.total?.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Any Undernutrition:</span>
                          <span className="font-bold text-red-600">{item.any_undernutrition_pct}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Severe Stunting:</span>
                          <span className="font-bold text-orange-600">{item.severe_stunting_pct}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">Severe Underweight:</span>
                          <span className="font-bold text-yellow-600">{item.severe_underweight_pct}%</span>
                        </div>
                        <div className="flex justify-between items-center">
                          <span className="text-gray-600">SAM:</span>
                          <span className="font-bold text-red-700">{item.sam_pct}%</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>

              {/* 0-6 Month Sample Insights */}
              <Card className="border-purple-300 bg-purple-50">
                <CardHeader>
                  <CardTitle className="text-purple-900">👶 0-6 Months High-Risk Analysis (Sample)</CardTitle>
                  <CardDescription>State-level sample showing extreme vulnerability in first 6 months</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="bg-white p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3">Stunting</h4>
                      <div className="text-3xl font-bold text-orange-600 mb-2">
                        {sample0_6m?.stunting?.stunted_pct}%
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p>Normal: {sample0_6m?.stunting?.normal}</p>
                        <p>Moderate: {sample0_6m?.stunting?.moderate}</p>
                        <p>Severe: {sample0_6m?.stunting?.severe}</p>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3">Underweight</h4>
                      <div className="text-3xl font-bold text-yellow-600 mb-2">
                        {sample0_6m?.underweight?.underweight_pct}%
                      </div>
                      <div className="space-y-1 text-sm text-gray-600">
                        <p>Normal: {sample0_6m?.underweight?.normal}</p>
                        <p>Moderate: {sample0_6m?.underweight?.moderate}</p>
                      </div>
                    </div>
                    
                    <div className="bg-white p-4 rounded-lg">
                      <h4 className="font-semibold text-gray-900 mb-3">Sample Size</h4>
                      <div className="text-3xl font-bold text-purple-600 mb-2">
                        {sample0_6m?.total_sample}
                      </div>
                      <p className="text-sm text-gray-600">infants tracked</p>
                      <p className="text-xs text-purple-700 mt-2">Sample from Dumra & Navrangpura-Paldi</p>
                    </div>
                  </div>
                  <div className="mt-4 p-3 bg-purple-100 rounded-lg text-sm text-purple-900">
                    💡 <strong>Key Insight:</strong> High stunting & underweight in 0-6 months indicates urgent need for 
                    maternal nutrition, birth weight monitoring, and early breastfeeding interventions.
                  </div>
                </CardContent>
              </Card>

              {/* Priority Children Table */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                    High-Risk Priority Children (Top 50)
                  </CardTitle>
                  <CardDescription>Children with severe malnutrition requiring immediate intervention</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-semibold">Name</th>
                          <th className="px-4 py-3 text-center font-semibold">Age Group</th>
                          <th className="px-4 py-3 text-center font-semibold">Gender</th>
                          <th className="px-4 py-3 text-left font-semibold">Sector</th>
                          <th className="px-4 py-3 text-left font-semibold">AWC</th>
                          <th className="px-4 py-3 text-center font-semibold">Stunted</th>
                          <th className="px-4 py-3 text-center font-semibold">Underweight</th>
                          <th className="px-4 py-3 text-center font-semibold">Wasted</th>
                        </tr>
                      </thead>
                      <tbody>
                        {priorityChildren.slice(0, 20).map((child, index) => (
                          <tr key={index} className="border-b hover:bg-gray-50">
                            <td className="px-4 py-3 font-medium">{child.beneficiary_name || 'N/A'}</td>
                            <td className="px-4 py-3 text-center text-xs">
                              {child.beneficiary_type?.replace('children_', '').replace('0m_6m', '0-6m').replace('6m_3y', '6m-3y').replace('3y_6y', '3-6y')}
                            </td>
                            <td className="px-4 py-3 text-center">{child.gender}</td>
                            <td className="px-4 py-3">{child.sector_name}</td>
                            <td className="px-4 py-3 text-xs">{child.awc_name}</td>
                            <td className="px-4 py-3 text-center">
                              {child.stunted === 'severely stunted' && (
                                <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs">Severe</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {child.underweight === 'severely underweight' && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs">Severe</span>
                              )}
                            </td>
                            <td className="px-4 py-3 text-center">
                              {child.wasted === 'sam' && (
                                <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-bold">SAM</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {priorityChildren.length > 20 && (
                    <p className="mt-4 text-sm text-gray-600 text-center">
                      Showing 20 of {priorityChildren.length} high-risk children
                    </p>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* District Detail Modal */}
      <THRDistrictDetailModal
        district={selectedDistrict}
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setSelectedDistrict(null);
        }}
        reportingPeriod="Oct-2025"
        dashboardType="growth-monitoring"
      />
    </div>
  );
};

export default GrowthMonitoringDashboard;
